import React from 'react'
import PropTypes from 'prop-types'
import { Button, Tag } from 'antd'
import {
  DeleteOutlined,
  EditOutlined,
  ScissorOutlined,
  SettingOutlined
} from '@ant-design/icons'

// The car's photos as cards instead of a table, for working on a phone.
//
// The table puts its four actions in a 160px column pinned to the right, as four bare <span>s with
// no padding — hit targets the height of the text itself, reached by scrolling a table sideways.
// The thumbnail's only preview is a hover Popover, which a touch screen has no equivalent of.
//
// The order is the order it is given. There is no position field on a car image anywhere in the
// system: the table renders whatever order the API returns, so this renders the same array the same
// way rather than inventing a sort that would disagree with the desktop view.
const ImageCards = props => {
  const {
    images,
    onUpdate,
    onCrop,
    onEdit,
    onDelete,
    onPreview
  } = props

  if (!images.length) {
    return (
      <div className="image-cards__empty">
        No photos on this car yet. Use <b>Add New Image</b> or <b>Bulk Upload</b> above.
      </div>
    )
  }

  return (
    <div className="image-cards">
      {images.map((image, index) => (
        <div className="image-cards__card" key={image.id || index}>
          <div className="image-cards__top">
            <button
              type="button"
              className="image-cards__thumb"
              onClick={() => onPreview(index)}
              aria-label={`Open photo ${index + 1} full size`}
            >
              {image.isVideo ? (
                <span className="image-cards__video">▶ Video</span>
              ) : (
                <img src={image.image} alt="" loading="lazy" />
              )}
            </button>
            <div className="image-cards__meta">
              {/* One string, not "#" + {index}: React otherwise splits them with a comment node,
                  so the rendered markup reads "#<!-- -->1" and cannot be matched or selected. */}
              <div className="image-cards__position">{`#${index + 1}`}</div>
              <div className="image-cards__tags">
                {image.isShowcaseImage ? <Tag color="gold">Showcase</Tag> : null}
                <Tag color={image.active ? 'green' : 'default'}>
                  {image.active ? 'Active' : 'Not active'}
                </Tag>
                {image.isVideo ? <Tag color="blue">Video</Tag> : null}
              </div>
              {image.isVideo && image.videoId ? (
                <div className="image-cards__videoid">ID: {image.videoId}</div>
              ) : null}
            </div>
          </div>

          {/* Every action a full-width row of its own: nothing to aim at, and no sideways scroll. */}
          <div className="image-cards__actions">
            <Button
              block
              size="large"
              icon={<SettingOutlined />}
              onClick={() => onUpdate(image)}
            >
              Update
            </Button>
            <Button
              block
              size="large"
              icon={<ScissorOutlined />}
              onClick={() => onCrop(image)}
            >
              Crop
            </Button>
            <Button
              block
              size="large"
              icon={<EditOutlined />}
              onClick={() => onEdit(image)}
            >
              Blur / draw
            </Button>
            <Button
              block
              size="large"
              danger
              icon={<DeleteOutlined />}
              onClick={() => onDelete(image)}
            >
              Delete
            </Button>
          </div>
        </div>
      ))}
    </div>
  )
}

ImageCards.propTypes = {
  images: PropTypes.array.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onCrop: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired
}

export default ImageCards
